<script setup>
import { ref, computed } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import SettingsTabs from '@/Components/UI/SettingsTabs.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import UiCard from '@/Components/UI/UiCard.vue'
import ConfirmDialog from '@/Components/UI/ConfirmDialog.vue'
import OfficeFormModal from '@/Components/Offices/OfficeFormModal.vue'

defineOptions({ layout: AppLayout })

const props = defineProps({
  offices: { type: Array, default: () => [] },
})

const { t } = useI18n()
const page = usePage()
const isAdmin = computed(() => page.props.auth?.user?.role === 'admin')

const modalOpen        = ref(false)
const editing          = ref(null)
const confirmDeleteId  = ref(null)

const openCreate = () => { editing.value = null; modalOpen.value = true }
const openEdit   = (o) => { editing.value = o;    modalOpen.value = true }
const closeModal = () => { modalOpen.value = false; editing.value = null }

const setDefault = (o) => {
  router.patch(`/offices/${o.id}/set_default`, {}, { preserveScroll: true })
}

const askDelete     = (o) => { confirmDeleteId.value = o.id }
const confirmDelete = () => {
  router.delete(`/offices/${confirmDeleteId.value}`, { preserveScroll: true })
  confirmDeleteId.value = null
}
const cancelDelete  = () => { confirmDeleteId.value = null }
</script>

<template>
  <PageShell :title="t('settings.title')">
    <template #actions>
      <UiButton
        v-if="isAdmin"
        variant="primary"
        icon-left="plus"
        @click="openCreate"
      >
        {{ t('offices.new') }}
      </UiButton>
    </template>

    <SettingsTabs />

    <div class="space-y-3">
      <UiCard
        v-for="o in offices"
        :key="o.id"
      >
        <div class="flex items-start justify-between gap-3 flex-wrap">
          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <h3 class="font-display text-base text-[color:var(--ink)] truncate">
                {{ o.name }}
              </h3>
              <span
                v-if="o.is_default"
                class="px-2 py-0.5 rounded-full text-xs font-medium"
                :style="{ background: 'var(--accentSoft)', color: 'var(--accent)' }"
              >
                {{ t('offices.default') }}
              </span>
            </div>
            <p
              v-if="o.address"
              class="text-sm text-[color:var(--inkSoft)] mt-1"
            >
              {{ o.address }}
            </p>
            <p
              v-else
              class="text-sm text-[color:var(--muted)] mt-1"
            >
              {{ t('offices.noAddress') }}
            </p>
            <p class="text-xs text-[color:var(--muted)] mt-1">
              {{ t('offices.rentalCount', { n: o.rentals_count }) }}
            </p>
          </div>

          <div
            v-if="isAdmin"
            class="flex items-center gap-2 flex-wrap flex-shrink-0"
          >
            <UiButton
              v-if="!o.is_default"
              size="sm"
              variant="secondary"
              @click="setDefault(o)"
            >
              {{ t('offices.actions.setDefault') }}
            </UiButton>
            <UiButton
              size="sm"
              variant="secondary"
              @click="openEdit(o)"
            >
              {{ t('common.edit') }}
            </UiButton>
            <UiButton
              size="sm"
              variant="ghost"
              @click="askDelete(o)"
            >
              {{ t('common.delete') }}
            </UiButton>
          </div>
        </div>
      </UiCard>

      <p
        v-if="!offices.length"
        class="text-sm text-[color:var(--muted)] text-center py-8"
      >
        {{ t('offices.empty') }}
      </p>
    </div>

    <OfficeFormModal
      :open="modalOpen"
      :office="editing"
      @close="closeModal"
    />

    <ConfirmDialog
      v-if="confirmDeleteId"
      :open="!!confirmDeleteId"
      :title="t('offices.actions.delete')"
      :consequence="t('offices.deleteConfirm')"
      danger
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </PageShell>
</template>
